/**
 * The per-column field picker for a group data import.
 *
 * A customer's spreadsheet carries whatever headings the person who built it
 * chose, so the import resolves the ones it recognizes and leaves the rest for
 * the submitter to point at a field by hand — rather than sending the document
 * back to be renamed. This is that choice: one picker per heading the document
 * carries, pre-filled with whatever the import already worked out.
 *
 * Contains:
 * - ColumnMappingFields — the picker grid (default export)
 */
import { Box, ListSubheader, MenuItem, TextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IGNORED_FIELD, fieldsClaimedElsewhere, groupCatalogByScope, normalizeImportHeader } from './groupImport';

/**
 * One field's option label, marking the ones a document must supply.
 *
 * @param {object} field - one entry of the import field catalog.
 * @returns {React.ReactNode} The label as the picker renders it.
 */
function FieldOptionLabel({ field }) {
  return (
    <Box component="span">
      {field.label}
      {field.required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' *'}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Render a picker for every column of an uploaded document.
 *
 * @param {object} props
 * @param {object[]} props.columns - the picker model from buildColumnMappings.
 * @param {object[]} props.catalog - the import field catalog.
 * @param {boolean} props.disabled - whether the dialog is mid-request.
 * @param {(heading: string, fieldName: string) => void} props.onChange - called
 *   with the column retargeted and the field it should fill.
 * @param {Object<string, string>} props.scopeLabels - the tenant's own word for
 *   each field scope, keyed by scope.
 * @returns {React.ReactElement} The picker grid.
 */
function ColumnMappingFields({ columns, catalog, disabled, onChange, scopeLabels }) {
  const { t } = useTranslation();
  const scopeGroups = useMemo(() => groupCatalogByScope(catalog), [catalog]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        columnGap: 2,
        rowGap: 1.5,
      }}
    >
      {columns.map(column => {
        const claimed = fieldsClaimedElsewhere(columns, column.heading);
        return (
          <Box key={normalizeImportHeader(column.heading)}>
            {/* The heading is the document's own text, so it is shown as the
                document wrote it. */}
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5, wordBreak: 'break-word' }}>
              {column.heading}
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              disabled={disabled}
              value={column.field}
              onChange={event => onChange(column.heading, event.target.value)}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ 'aria-label': column.heading }}
            >
              <MenuItem value={IGNORED_FIELD} disabled={!column.canIgnore}>
                <em>{t('dataImports.columnNotImported', 'Not imported')}</em>
              </MenuItem>
              {scopeGroups.map(group => [
                <ListSubheader key={group.scope}>{scopeLabels[group.scope] ?? group.scope}</ListSubheader>,
                ...group.fields.map(field => (
                  <MenuItem
                    key={field.name}
                    value={field.name}
                    // A field another column already fills is refused here
                    // rather than sent and overruled by the reader.
                    disabled={field.name !== column.field && claimed.has(field.name)}
                  >
                    <FieldOptionLabel field={field} />
                  </MenuItem>
                )),
              ])}
            </TextField>
          </Box>
        );
      })}
    </Box>
  );
}

export default ColumnMappingFields;
